import React, { useDeferredValue, useEffect, useMemo, useRef, useState, } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Award, CalendarDays, CheckCircle2, Clock3, Download, Eye, Filter, Grid2X2, Layers3, List, RefreshCw, RotateCcw, Search, ShieldAlert, Trash2, X, XCircle, } from "lucide-react";
import { Document, Page, pdfjs } from "react-pdf";
import { useLanguage } from "../context/LanguageContext";
import SecretCodeModal from "../components/SecretCodeModal";
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
const API_URL = window.location.hostname === "localhost"
    ? "http://localhost:3001"
    : "https://api.certilink.tn";
const getParticipantName = (participant) => participant?.fullName ||
    [participant?.firstName, participant?.lastName]
        .filter(Boolean)
        .join(" ")
        .trim() ||
    participant?.name ||
    "Participant";
const formatDate = (value) => {
    if (!value)
        return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime()))
        return "—";
    return new Intl.DateTimeFormat((document.documentElement.lang === "fr" ? "fr-FR" : "en-GB"), {
        day: "2-digit",
        month: "short",
        year: "numeric",
    }).format(date);
};
const getYear = (certificate) => {
    const date = new Date(certificate?.start_date || certificate?.createdAt || 0);
    return Number.isNaN(date.getTime()) ? null : date.getFullYear();
};
const Certifs = () => {
    const { participantId } = useParams();
    const navigate = useNavigate();
    const { t, lang } = useLanguage();
    const [certificates, setCertificates] = useState([]);
    const [participantName, setParticipantName] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const deferredSearchTerm = useDeferredValue(searchTerm);
    const [showFilters, setShowFilters] = useState(false);
    const [themeFilter, setThemeFilter] = useState("all");
    const [resultFilter, setResultFilter] = useState("all");
    const [yearFilter, setYearFilter] = useState("all");
    const [sortBy, setSortBy] = useState("newest");
    const [viewMode, setViewMode] = useState("list");
    const [previewUrls, setPreviewUrls] = useState({});
    const [loadingPreviews, setLoadingPreviews] = useState({});
    const [zoomedCert, setZoomedCert] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deletionCode, setDeletionCode] = useState("");
    const [deletingCertificate, setDeletingCertificate] = useState(false);
    const createdUrlsRef = useRef([]);
    const tr = (key, fallback) => t?.ui?.certifs?.[key] || t?.[key] || fallback;
    useEffect(() => {
        const fetchCertifs = async () => {
            setLoading(true);
            setError("");
            try {
                const token = localStorage.getItem("token");
                if (!token) {
                    navigate("/login", { replace: true });
                    return;
                }
                const response = await fetch(`${API_URL}/api/certifs/view/${participantId}`, { headers: { Authorization: `Bearer ${token}` } });
                const data = await response.json().catch(() => ({}));
                if (!response.ok) {
                    throw new Error(data.message ||
                        (response.status === 403
                            ? tr("accessError", "Vous n\u2019avez pas acc\u00E8s \u00E0 ces certificats.") : tr("loadError", "Impossible de charger les certificats.")));
                }
                const certificateList = Array.isArray(data) ? data : [];
                setCertificates(certificateList);
                if (certificateList[0]?.participant) {
                    setParticipantName(getParticipantName(certificateList[0].participant));
                }
                else {
                    const participantResponse = await fetch(`${API_URL}/api/participants/${participantId}`, { headers: { Authorization: `Bearer ${token}` } });
                    if (participantResponse.ok) {
                        const participant = await participantResponse.json();
                        setParticipantName(getParticipantName(participant));
                    }
                }
            }
            catch (fetchError) {
                console.error("Certificates fetch error:", fetchError);
                setError(fetchError.message || tr("loadingError", "Erreur lors du chargement"));
            }
            finally {
                setLoading(false);
            }
        };
        fetchCertifs();
    }, [navigate, participantId]);
    useEffect(() => {
        return () => {
            createdUrlsRef.current.forEach((url) => {
                try {
                    URL.revokeObjectURL(url);
                }
                catch {
                    // URL déjà libérée.
                }
            });
            createdUrlsRef.current = [];
        };
    }, []);
    const filterOptions = useMemo(() => {
        const themes = new Set();
        const years = new Set();
        certificates.forEach((certificate) => {
            if (certificate.theme)
                themes.add(certificate.theme);
            const year = getYear(certificate);
            if (year)
                years.add(String(year));
        });
        return {
            themes: [...themes].sort((a, b) => a.localeCompare(b, "fr", { sensitivity: "base" })),
            years: [...years].sort((a, b) => Number(b) - Number(a)),
        };
    }, [certificates]);
    const filteredCertificates = useMemo(() => {
        const query = deferredSearchTerm.trim().toLocaleLowerCase("fr");
        const result = certificates.filter((certificate) => {
            const searchableText = [
                certificate.identifier,
                certificate.theme,
                certificate.subTheme,
                certificate.duration,
                certificate.templateModel,
            ]
                .filter((value) => value !== undefined && value !== null)
                .join(" ")
                .toLocaleLowerCase("fr");
            return ((!query || searchableText.includes(query)) &&
                (themeFilter === "all" || certificate.theme === themeFilter) &&
                (resultFilter === "all" ||
                    (resultFilter === "success" && Boolean(certificate.success)) ||
                    (resultFilter === "participation" && !certificate.success)) &&
                (yearFilter === "all" || String(getYear(certificate)) === yearFilter));
        });
        return [...result].sort((a, b) => {
            if (sortBy === "oldest") {
                return new Date(a.start_date || 0) - new Date(b.start_date || 0);
            }
            if (sortBy === "theme") {
                return String(a.theme || "").localeCompare(String(b.theme || ""), "fr");
            }
            if (sortBy === "duration") {
                return Number(b.duration || 0) - Number(a.duration || 0);
            }
            return new Date(b.start_date || b.createdAt || 0) -
                new Date(a.start_date || a.createdAt || 0);
        });
    }, [
        certificates,
        deferredSearchTerm,
        resultFilter,
        sortBy,
        themeFilter,
        yearFilter,
    ]);
    const activeFilterCount = [
        themeFilter !== "all",
        resultFilter !== "all",
        yearFilter !== "all",
    ].filter(Boolean).length;
    const resetFilters = () => {
        setSearchTerm("");
        setThemeFilter("all");
        setResultFilter("all");
        setYearFilter("all");
        setSortBy("newest");
    };
    const fetchPreview = async (id) => {
        if (!id || previewUrls[id] || loadingPreviews[id])
            return previewUrls[id];
        setLoadingPreviews((current) => ({ ...current, [id]: true }));
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_URL}/api/certifs/download/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                throw new Error(data.message || tr("downloadFailed", "Téléchargement impossible"));
            }
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            createdUrlsRef.current.push(url);
            setPreviewUrls((current) => ({ ...current, [id]: url }));
            return url;
        }
        catch (previewError) {
            console.error("Preview fetch error:", previewError);
            alert(previewError.message);
            return null;
        }
        finally {
            setLoadingPreviews((current) => ({ ...current, [id]: false }));
        }
    };
    const openPreview = (certificate) => {
        setZoomedCert(certificate._id);
        fetchPreview(certificate._id);
    };
    const downloadCertificate = async (certificate, event) => {
        event?.stopPropagation();
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_URL}/api/certifs/download/${certificate._id}`, { headers: { Authorization: `Bearer ${token}` } });
            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                throw new Error(data.message || tr("downloadFailed", "Téléchargement impossible"));
            }
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `${participantName || "certificat"}-${certificate.identifier || certificate._id}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
        }
        catch (downloadError) {
            alert(`${tr("errorLabel", "Erreur")}: ${downloadError.message}`);
        }
    };
    const handleDelete = (certificate, event) => {
        event?.stopPropagation();
        setDeleteTarget(certificate);
        setDeletionCode("");
    };
    const confirmDeleteCertificate = async () => {
        if (!deleteTarget || !deletionCode.trim())
            return;
        setDeletingCertificate(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_URL}/api/certifs/${deleteTarget._id}`, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ secretCode: deletionCode }),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok)
                throw new Error(data.message || tr("deleteError", "Suppression impossible"));
            setCertificates((current) => current.filter((item) => item._id !== deleteTarget._id));
            if (previewUrls[deleteTarget._id]) {
                URL.revokeObjectURL(previewUrls[deleteTarget._id]);
            }
            setDeleteTarget(null);
            setDeletionCode("");
        }
        catch (deleteError) {
            alert(`${tr("errorLabel", "Erreur")}: ${deleteError.message}`);
        }
        finally {
            setDeletingCertificate(false);
        }
    };
    if (loading) {
        return (<div className="flex min-h-[60vh] items-center justify-center bg-slate-50">
        <div className="flex items-center gap-3 text-sm font-semibold text-slate-600">
          <RefreshCw className="h-5 w-5 animate-spin text-blue-600"/>
          {tr("loadingCertificates", "Chargement des certificats…")}
        </div>
      </div>);
    }
    if (error) {
        return (<div className="min-h-screen bg-slate-50 px-4 pt-24">
        <div className="mx-auto max-w-md rounded-3xl border border-rose-200 bg-white p-8 text-center shadow-xl shadow-slate-200/60">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-rose-50">
            <ShieldAlert className="h-7 w-7 text-rose-600"/>
          </span>
          <h2 className="mt-4 text-xl font-bold text-slate-900">
            {tr("accessDenied", "Accès impossible")}
          </h2>
          <p className="mt-2 text-sm text-slate-600">{error}</p>
          <button type="button" onClick={() => navigate("/participants")} className="mt-6 rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-800">{tr("backParticipants", "Retour aux participants")}</button>
        </div>
      </div>);
    }
    return (<div className="min-h-screen bg-slate-50 px-3 pb-8 pt-20 sm:px-5">
      <div className="mx-auto max-w-7xl">
        <div className="relative mb-5 overflow-hidden rounded-3xl bg-gradient-to-r from-slate-950 via-blue-950 to-slate-900 px-5 py-6 text-white shadow-xl shadow-slate-200/70 sm:px-7">
          <div className="absolute -right-12 -top-16 h-44 w-44 rounded-full bg-blue-500/20 blur-2xl"/>
          <div className="absolute -bottom-20 left-1/3 h-40 w-40 rounded-full bg-cyan-400/10 blur-2xl"/>
          <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-center gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/10 ring-1 ring-white/15">
                <Award className="h-6 w-6 text-cyan-300"/>
              </span>
              <div className="min-w-0">
                <h1 className="truncate text-2xl font-bold tracking-tight sm:text-3xl">{tr("certificatesOf", "Certificats de")}{participantName || tr("thisParticipant", "ce participant")}
                </h1>
                <p className="mt-1 text-sm text-slate-300">{tr("subtitle", "Cliquez sur une ligne ou une carte pour pr\u00E9visualiser le PDF.")}</p>
              </div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/10 px-4 py-3 text-left backdrop-blur sm:text-right">
              <div className="text-2xl font-bold text-white">
                {filteredCertificates.length}
              </div>
              <div className="text-xs font-medium uppercase tracking-wider text-slate-300">
                {certificates.length}{tr("total", "au total")}</div>
            </div>
          </div>
        </div>

        <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm shadow-slate-200/60">
          <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
            <div className="relative min-w-0 flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"/>
              <input type="search" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder={tr("searchPlaceholder", "Rechercher par identifiant, th\u00E8me ou sous-th\u00E8me\u2026")} className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 pl-10 pr-10 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-blue-400 focus:bg-white focus:ring-4 focus:ring-blue-100/60"/>
              {searchTerm ? (<button type="button" aria-label={tr("clearSearch", "Effacer la recherche")} onClick={() => setSearchTerm("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition hover:text-slate-700">
                  <X className="h-4 w-4"/>
                </button>) : null}
            </div>

            <select value={sortBy} onChange={(event) => setSortBy(event.target.value)} className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100/60">
              <option value="newest">{tr("sortNewest", "Plus r\u00E9cents")}</option>
              <option value="oldest">{tr("sortOldest", "Plus anciens")}</option>
              <option value="theme">{tr("sortTheme", "Th\u00E8me A\u2013Z")}</option>
              <option value="duration">{tr("sortDuration", "Dur\u00E9e d\u00E9croissante")}</option>
            </select>

            <button type="button" onClick={() => setShowFilters((current) => !current)} className={`inline-flex h-11 items-center justify-center gap-2 rounded-xl border px-4 text-sm font-semibold transition ${showFilters || activeFilterCount > 0
            ? "border-blue-200 bg-blue-50 text-blue-700"
            : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}>
              <Filter className="h-4 w-4"/>{tr("filters", "Filtres")}{activeFilterCount > 0 ? (<span className="rounded-full bg-blue-600 px-1.5 py-0.5 text-[10px] text-white">
                  {activeFilterCount}
                </span>) : null}
            </button>

            <div className="flex h-11 rounded-xl border border-slate-200 bg-slate-50 p-1">
              <button type="button" title={tr("listView", "Vue liste")} onClick={() => setViewMode("list")} className={`flex h-9 w-9 items-center justify-center rounded-lg transition ${viewMode === "list"
            ? "bg-white text-blue-700 shadow-sm"
            : "text-slate-500 hover:text-slate-800"}`}>
                <List className="h-4 w-4"/>
              </button>
              <button type="button" title={tr("gridView", "Vue grille")} onClick={() => setViewMode("grid")} className={`flex h-9 w-9 items-center justify-center rounded-lg transition ${viewMode === "grid"
            ? "bg-white text-blue-700 shadow-sm"
            : "text-slate-500 hover:text-slate-800"}`}>
                <Grid2X2 className="h-4 w-4"/>
              </button>
            </div>

            {(activeFilterCount > 0 || searchTerm) && (<button type="button" title={tr("reset", "R\u00E9initialiser")} onClick={resetFilters} className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600">
                <RotateCcw className="h-4 w-4"/>
              </button>)}
          </div>

          {showFilters ? (<div className="mt-3 grid grid-cols-1 gap-2 border-t border-slate-100 pt-3 sm:grid-cols-3">
              <select value={themeFilter} onChange={(event) => setThemeFilter(event.target.value)} className="h-10 rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-700 outline-none focus:border-blue-400 focus:bg-white focus:ring-4 focus:ring-blue-100/60">
                <option value="all">{tr("allThemes", "Tous les th\u00E8mes")}</option>
                {filterOptions.themes.map((theme) => (<option key={theme} value={theme}>
                    {theme}
                  </option>))}
              </select>

              <select value={resultFilter} onChange={(event) => setResultFilter(event.target.value)} className="h-10 rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-700 outline-none focus:border-blue-400 focus:bg-white focus:ring-4 focus:ring-blue-100/60">
                <option value="all">{tr("allResults", "Tous les r\u00E9sultats")}</option>
                <option value="success">{tr("successful", "R\u00E9ussi")}</option>
                <option value="participation">{tr("participation", "Participation")}</option>
              </select>

              <select value={yearFilter} onChange={(event) => setYearFilter(event.target.value)} className="h-10 rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-700 outline-none focus:border-blue-400 focus:bg-white focus:ring-4 focus:ring-blue-100/60">
                <option value="all">{tr("allYears", "Toutes les ann\u00E9es")}</option>
                {filterOptions.years.map((year) => (<option key={year} value={year}>
                    {year}
                  </option>))}
              </select>
            </div>) : null}
        </div>

        {filteredCertificates.length === 0 ? (<div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-16 text-center shadow-sm">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50">
              <Award className="h-7 w-7 text-blue-600"/>
            </span>
            <h2 className="mt-4 text-lg font-bold text-slate-900">
              {certificates.length === 0
                ? tr("noCertFound", "Aucun certificat")
                : tr("noFiltered", "Aucun r\u00E9sultat avec ces filtres")}
            </h2>
            <p className="mt-1 text-sm text-slate-500">{tr("adjustFilters", "Modifiez la recherche ou r\u00E9initialisez les filtres.")}</p>
          </div>) : viewMode === "grid" ? (<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filteredCertificates.map((certificate) => (<article key={certificate._id} role="button" tabIndex={0} onClick={() => openPreview(certificate)} onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        openPreview(certificate);
                    }
                }} className="group cursor-pointer overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:border-blue-200 hover:shadow-lg hover:shadow-blue-100/60 focus:outline-none focus:ring-4 focus:ring-blue-100">
                <div className="relative flex aspect-[1.414] items-center justify-center overflow-hidden bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900">
                  <div className="absolute -right-8 -top-8 h-28 w-28 rounded-full bg-cyan-400/15 blur-xl"/>
                  <Award className="h-14 w-14 text-cyan-300 transition group-hover:scale-105"/>
                  <span className="absolute bottom-3 left-3 rounded-full border border-white/10 bg-white/10 px-2.5 py-1 text-[11px] font-semibold text-slate-200 backdrop-blur">
                    {certificate.identifier || tr("certificate", "Certificat")}
                  </span>
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="truncate font-bold text-slate-900">
                        {certificate.theme || tr("noTheme", "Sans thème")}
                      </h3>
                      <p className="mt-1 line-clamp-2 min-h-10 text-sm text-slate-500">
                        {certificate.subTheme || tr("noSubTheme", "Sous-thème non renseigné")}
                      </p>
                    </div>
                    <span className={`shrink-0 rounded-full px-2 py-1 text-[10px] font-bold uppercase ring-1 ring-inset ${certificate.success
                    ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                    : "bg-cyan-50 text-cyan-700 ring-cyan-200"}`}>
                      {certificate.success ? tr("successful", "R\u00E9ussi") : tr("participation", "Participation")}
                    </span>
                  </div>
                  <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1.5">
                      <CalendarDays className="h-3.5 w-3.5"/>
                      {formatDate(certificate.start_date)}
                    </span>
                    <span className="inline-flex items-center gap-1.5 font-semibold text-slate-700">
                      <Clock3 className="h-3.5 w-3.5"/>
                      {certificate.duration || 0}{tr("daysShort", "j")}</span>
                  </div>
                  <div className="mt-3 flex gap-2">
                    <button type="button" onClick={(event) => downloadCertificate(certificate, event)} className="inline-flex h-9 flex-1 items-center justify-center gap-2 rounded-lg bg-blue-50 text-xs font-semibold text-blue-700 transition hover:bg-blue-100">
                      <Download className="h-3.5 w-3.5"/>{tr("pdf", "PDF")}</button>
                    <button type="button" onClick={(event) => handleDelete(certificate, event)} className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-rose-50 text-rose-600 transition hover:bg-rose-100" title={tr("delete", "Supprimer")}>
                      <Trash2 className="h-3.5 w-3.5"/>
                    </button>
                  </div>
                </div>
              </article>))}
          </div>) : (<div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg shadow-slate-200/50">
            <div className="hidden max-h-[68vh] overflow-auto md:block">
              <table className="w-full table-fixed text-left text-sm">
                <colgroup>
                  <col className="w-[19%]"/>
                  <col className="w-[25%]"/>
                  <col className="w-[21%]"/>
                  <col className="w-[10%]"/>
                  <col className="w-[11%]"/>
                  <col className="w-[14%]"/>
                </colgroup>
                <thead className="sticky top-0 z-10 bg-slate-900 text-xs uppercase tracking-wider text-slate-300 shadow-sm">
                  <tr>
                    <th className="px-4 py-3 font-semibold">{tr("certificate", "Certificat")}</th>
                    <th className="px-4 py-3 font-semibold">{tr("training", "Formation")}</th>
                    <th className="px-4 py-3 font-semibold">{tr("period", "P\u00E9riode")}</th>
                    <th className="px-4 py-3 text-center font-semibold">{tr("duration", "Dur\u00E9e")}</th>
                    <th className="px-4 py-3 text-center font-semibold">{tr("result", "R\u00E9sultat")}</th>
                    <th className="px-4 py-3 text-center font-semibold">{tr("actions", "Actions")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredCertificates.map((certificate) => (<tr key={certificate._id} role="button" tabIndex={0} onClick={() => openPreview(certificate)} onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        openPreview(certificate);
                    }
                }} className="cursor-pointer transition hover:bg-blue-50/80 focus:bg-blue-50/80 focus:outline-none">
                      <td className="px-4 py-3">
                        <div className="flex min-w-0 items-center gap-3">
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 text-white shadow-sm">
                            <Award className="h-4 w-4"/>
                          </span>
                          <div className="min-w-0">
                            <div className="truncate font-semibold text-slate-900" title={certificate.identifier || ""}>
                              {certificate.identifier || tr("noIdentifier", "Sans identifiant")}
                            </div>
                            <div className="mt-0.5 truncate text-xs text-slate-500">
                              {certificate.templateModel || tr("standardModel", "Modèle standard")}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="truncate font-medium text-slate-800" title={certificate.theme || ""}>
                          {certificate.theme || "—"}
                        </div>
                        <div className="mt-1 truncate text-xs text-slate-500" title={certificate.subTheme || ""}>
                          {certificate.subTheme || tr("noSubTheme", "Sous-thème non renseigné")}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 text-slate-700">
                          <CalendarDays className="h-3.5 w-3.5 shrink-0 text-blue-500"/>
                          <span className="truncate">
                            {formatDate(certificate.start_date)}
                          </span>
                        </div>
                        <div className="mt-1 pl-5 text-xs text-slate-500">{tr("to", "au")}{formatDate(certificate.end_date)}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-700">
                          <Clock3 className="h-3.5 w-3.5"/>
                          {certificate.duration || 0}{tr("daysShort", "j")}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold ring-1 ring-inset ${certificate.success
                    ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                    : "bg-cyan-50 text-cyan-700 ring-cyan-200"}`}>
                          {certificate.success ? (<CheckCircle2 className="h-3.5 w-3.5"/>) : (<Layers3 className="h-3.5 w-3.5"/>)}
                          {certificate.success ? tr("successful", "R\u00E9ussi") : tr("participation", "Participation")}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-center gap-1.5">
                          <button type="button" title={tr("preview", "Pr\u00E9visualiser")} onClick={(event) => {
                    event.stopPropagation();
                    openPreview(certificate);
                }} className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700">
                            <Eye className="h-4 w-4"/>
                          </button>
                          <button type="button" title={tr("download", "T\u00E9l\u00E9charger")} onClick={(event) => downloadCertificate(certificate, event)} className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-blue-200 bg-blue-50 text-blue-700 transition hover:bg-blue-100">
                            <Download className="h-4 w-4"/>
                          </button>
                          <button type="button" title={tr("delete", "Supprimer")} onClick={(event) => handleDelete(certificate, event)} className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-rose-200 text-rose-600 transition hover:bg-rose-50">
                            <Trash2 className="h-4 w-4"/>
                          </button>
                        </div>
                      </td>
                    </tr>))}
                </tbody>
              </table>
            </div>

            <div className="divide-y divide-slate-100 md:hidden">
              {filteredCertificates.map((certificate) => (<article key={certificate._id} role="button" tabIndex={0} onClick={() => openPreview(certificate)} onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        openPreview(certificate);
                    }
                }} className="cursor-pointer p-4 transition hover:bg-blue-50 focus:bg-blue-50 focus:outline-none">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="truncate font-bold text-slate-900">
                        {certificate.theme || tr("noTheme", "Sans thème")}
                      </div>
                      <div className="mt-1 line-clamp-2 text-sm text-slate-600">
                        {certificate.subTheme || tr("noSubTheme", "Sous-thème non renseigné")}
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                        <span>{formatDate(certificate.start_date)}</span>
                        <span>•</span>
                        <span>{certificate.duration || 0} {tr("days", "jours")}</span>
                      </div>
                    </div>
                    <span className={`shrink-0 rounded-full px-2 py-1 text-[10px] font-bold uppercase ring-1 ring-inset ${certificate.success
                    ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                    : "bg-cyan-50 text-cyan-700 ring-cyan-200"}`}>
                      {certificate.success ? tr("successful", "R\u00E9ussi") : tr("participation", "Participation")}
                    </span>
                  </div>
                  <div className="mt-3 flex gap-2 border-t border-slate-100 pt-3">
                    <button type="button" onClick={(event) => downloadCertificate(certificate, event)} className="inline-flex h-9 flex-1 items-center justify-center gap-2 rounded-lg bg-blue-50 text-xs font-semibold text-blue-700">
                      <Download className="h-3.5 w-3.5"/>{tr("download", "T\u00E9l\u00E9charger")}</button>
                    <button type="button" onClick={(event) => handleDelete(certificate, event)} className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-rose-50 text-rose-600">
                      <Trash2 className="h-3.5 w-3.5"/>
                    </button>
                  </div>
                </article>))}
            </div>
          </div>)}
      </div>

      <SecretCodeModal
        open={Boolean(deleteTarget)}
        title={tr("deleteCertificateTitle", "Supprimer ce certificat")}
        description={tr("deleteConfirm", "Supprimer le certificat {identifier} ?").replace("{identifier}", deleteTarget?.identifier || "")}
        code={deletionCode}
        setCode={setDeletionCode}
        onClose={() => {
            if (!deletingCertificate) {
                setDeleteTarget(null);
                setDeletionCode("");
            }
        }}
        onConfirm={confirmDeleteCertificate}
        loading={deletingCertificate}
        labels={{
            protectedDeletion: tr("protectedDeletion", "Suppression protégée"),
            codeLabel: tr("deletionCodeLabel", "Code de suppression de l’administrateur"),
            codePlaceholder: tr("deletionCodePlaceholder", "Saisissez le code de suppression"),
            cancel: tr("cancel", "Annuler"),
            confirm: tr("confirmDelete", "Confirmer la suppression"),
            deleting: tr("deleting", "Suppression…"),
            close: tr("close", "Fermer"),
        }}
      />

      {zoomedCert ? (<div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/85 p-3 backdrop-blur-sm sm:p-6" onClick={() => setZoomedCert(null)}>
          <div className="relative flex h-[min(88vh,760px)] w-full max-w-5xl items-center justify-center overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <button type="button" title={tr("close", "Fermer")} onClick={() => setZoomedCert(null)} className="absolute right-3 top-3 z-20 inline-flex h-10 w-10 items-center justify-center rounded-full bg-slate-900/80 text-white transition hover:bg-slate-900">
              <X className="h-5 w-5"/>
            </button>

            {loadingPreviews[zoomedCert] || !previewUrls[zoomedCert] ? (<div className="flex flex-col items-center gap-3 text-sm font-semibold text-slate-600">
                <RefreshCw className="h-7 w-7 animate-spin text-blue-600"/>{tr("preparingPreview", "Pr\u00E9paration de l\u2019aper\u00E7u\u2026")}</div>) : (<div className="h-full w-full overflow-auto bg-slate-100 p-3 sm:p-5">
                <Document file={previewUrls[zoomedCert]} className="mx-auto flex min-h-full items-start justify-center" loading={<div className="flex h-full items-center justify-center">
                      <RefreshCw className="h-7 w-7 animate-spin text-blue-600"/>
                    </div>}>
                  <Page pageNumber={1} renderTextLayer={false} renderAnnotationLayer={false} className="overflow-hidden rounded-lg shadow-xl [&>canvas]:!h-auto [&>canvas]:!max-w-full"/>
                </Document>
              </div>)}
          </div>
        </div>) : null}
    </div>);
};
export default Certifs;
